import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { ChevronRight, ChevronDown, Folder, FolderOpen, Eye, EyeOff, Trash2, FolderPlus, SunDim, Sun, ScanEye, ScanLine, Pencil, Palette, GripVertical, Info, Crosshair, Edit3, Copy, Download, FolderInput, MapPin, Ruler, Route, Hexagon, Type } from 'lucide-react';
import { exportUserArtifact, exportFolder } from '../../utils/userArtifactExport';
import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCenter, useDroppable, useSensor, useSensors, } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, } from '@dnd-kit/sortable';
import { ColorPicker } from '../shared/ColorPicker';
import { Avatar } from '../Avatar';
import { getDisplayName } from '../../utils/commentAvatar';
import { useContextMenu } from '../ContextMenu/useContextMenu';
import { ContextMenu } from '../ContextMenu/ContextMenu';
import { SliderChip, SliderUnderBar } from './SliderChip';
import { canModifyArtifact as canModifyArtifactFor } from '../../utils/capabilities';
// The attribution face itself. Sized to the row's type icon so adding it
// never changes the row height, and it never carries a word.
const AuthorChip = ({ author, title }) => (_jsx(Avatar, { userId: author.userId, commentColor: author.commentColor, photoUrl: author.avatarUrl, source: author, size: 14, title: title }));
// Max nesting depth (root = 1). Mirrors the backend's maxFolderDepth in
// ops/folder/ops.go; kept here so the UI can preview validity (e.g. hiding
// the "+ Subfolder" button at depth 3) without a server round-trip.
const MAX_FOLDER_DEPTH = 4;
// Inline sortable wrapper for an artifact row. The hook's setNodeRef goes on
// the outer wrapper (so dnd-kit can compute transforms for the row); the
// drag handle alone gets the pointer/keyboard listeners so the rest of the
// row stays clickable. Handle is hover-revealed for low visual noise.
//
// `isOver` from useSortable is repurposed as a drop indicator: when another
// item is being dragged onto this row, render a thin top accent line so the
// user can see exactly where the dragged item will land (rather than relying
// solely on the implicit shift animation).
const SortableArtifactRow = ({ id, disabled, dropTargetDisabled, children }) => {
    const sortableDisabled = dropTargetDisabled
        ? { droppable: true, draggable: !!disabled }
        : disabled;
    const { attributes, listeners, setNodeRef, isDragging, isOver, activeIndex, index } = useSortable({ id, disabled: sortableDisabled });
    // Direction-aware drop indicator: matches arrayMove(activeIndex, index)
    // semantics. Downward (activeIndex < index) → BOTTOM accent; upward → TOP.
    const sameSortable = activeIndex >= 0;
    const showIndicator = isOver && !isDragging && sameSortable && activeIndex !== index;
    const indicatorSide = !showIndicator
        ? null
        : activeIndex < index ? 'bottom' : 'top';
    // Option 2: rows do NOT move during drag. The source row stays put as a
    // dim placeholder; a cursor-following <DragOverlay> chip + the DropLine
    // handle "moving" and "where to". No transform / no transition on the row
    // means zero re-rasterising of rich nested contents → no warping.
    const style = {
        opacity: isDragging ? 0.3 : 1,
        borderRadius: 4,
        position: 'relative',
    };
    const dragHandle = disabled ? null : (_jsx("button", { type: "button", ...attributes, ...listeners, className: "touch-none cursor-grab active:cursor-grabbing opacity-30 group-hover:opacity-100 transition-opacity", style: { color: 'var(--dew)', background: 'none', border: 'none', padding: 2 }, "aria-label": "Drag to reorder", title: "Drag to reorder", onClick: (e) => e.preventDefault(), children: _jsx(GripVertical, { size: 12 }) }));
    return (_jsxs("div", { ref: setNodeRef, style: style, children: [children(dragHandle), indicatorSide && _jsx(DropLine, { side: indicatorSide })] }));
};
// True between-row drop indicator. Sits absolutely-positioned in the gap
// above or below the row (outside its bounds) so it reads as a horizontal
// line *between* features rather than an accent hugging a single row's edge.
const DropLine = ({ side }) => (_jsx("div", { "aria-hidden": true, style: {
        position: 'absolute',
        left: 4,
        right: 4,
        height: 2,
        borderRadius: 1,
        backgroundColor: 'var(--fern, #5db075)',
        boxShadow: '0 0 0 1px rgba(93, 176, 117, 0.25)',
        pointerEvents: 'none',
        ...(side === 'top' ? { top: -3 } : { bottom: -3 }),
    } }));
// Sortable wrapper for the folder header so users can drag folders to
// reorder them within the project. Same drag-handle pattern as the artifact
// row — hover-revealed grip on the left, body stays clickable.
const SortableFolderHeader = ({ id, disabled, children }) => {
    const { attributes, listeners, setNodeRef, isDragging, isOver, activeIndex, index } = useSortable({ id, disabled });
    // Same direction-aware indicator as SortableArtifactRow — see comment there
    // for the arrayMove semantics behind the top/bottom choice.
    const sameSortable = activeIndex >= 0;
    const showIndicator = isOver && !isDragging && sameSortable && activeIndex !== index;
    const indicatorSide = !showIndicator
        ? null
        : activeIndex < index ? 'bottom' : 'top';
    // Option 2 styling — see SortableArtifactRow for the rationale.
    const style = {
        opacity: isDragging ? 0.3 : 1,
        position: 'relative',
    };
    const dragHandle = disabled ? null : (_jsx("button", { type: "button", ...attributes, ...listeners, className: "touch-none cursor-grab active:cursor-grabbing opacity-30 group-hover:opacity-100 transition-opacity", style: { color: 'var(--dew)', background: 'none', border: 'none', padding: 2 }, "aria-label": "Drag to reorder folder", title: "Drag to reorder folder", onClick: (e) => e.preventDefault(), children: _jsx(GripVertical, { size: 12 }) }));
    return (_jsxs("div", { ref: setNodeRef, style: style, children: [children(dragHandle), indicatorSide && _jsx(DropLine, { side: indicatorSide })] }));
};
// Wraps an entire folder (header + body) as a single drop target. The whole
// folder is one zone so dropping anywhere on it routes to the same destination
// folder, regardless of whether the user lands on a specific artifact, on
// empty body space, or on the header (useful for collapsed folders). The
// folder-id is namespaced with "folder-" to avoid colliding with artifact IDs
// in the dnd-kit registry.
const DroppableFolder = ({ folderId, isCrossFolderDrag, forceHighlight, isCollapsed, onAutoExpand, disabled, children }) => {
    const { isOver, setNodeRef } = useDroppable({ id: `folder-${folderId}`, disabled });
    const expandTimerRef = useRef(null);
    // The cursor is "effectively over" this folder when either dnd-kit reports
    // its droppable as the over target OR the parent has resolved this folder
    // as the drop destination via the over.id heuristic.
    const effectivelyOver = isOver || forceHighlight;
    useEffect(() => {
        // Auto-expand: after the cursor lingers ~500ms over a collapsed folder
        // during a cross-folder drag, expand it so the user can see the
        // landing area before they release. Cancelled on cursor leave / drop /
        // unmount.
        if (effectivelyOver && isCrossFolderDrag && isCollapsed) {
            expandTimerRef.current = window.setTimeout(() => {
                onAutoExpand();
                expandTimerRef.current = null;
            }, 500);
        }
        return () => {
            if (expandTimerRef.current !== null) {
                window.clearTimeout(expandTimerRef.current);
                expandTimerRef.current = null;
            }
        };
    }, [effectivelyOver, isCrossFolderDrag, isCollapsed, onAutoExpand]);
    const highlight = isCrossFolderDrag && effectivelyOver;
    return (_jsx("div", { ref: setNodeRef, style: {
            borderRadius: 6,
            backgroundColor: highlight ? 'rgba(80, 160, 255, 0.10)' : undefined,
            outline: highlight ? '1px dashed rgba(80, 160, 255, 0.7)' : undefined,
            outlineOffset: -1,
            transition: 'background-color 0.15s, outline-color 0.15s',
        }, children: children }));
};
// Per-type lucide icon shown in the row. Mirrors UnsavedDrawingsSection so
// saved + unsaved features read with the same visual vocabulary.
const ARTIFACT_TYPE_ICONS = {
    placemark: MapPin,
    measurement: Ruler,
    path: Route,
    polygon: Hexagon,
    label: Type,
    sketch: Pencil,
};
export const UserFolderSection = ({ folders, isLoading, error, folderVisibility, folderCollapsed, onToggleFolderVisibility, onToggleFolderCollapsed, onCreateFolder, onUpdateFolder, onDeleteFolder, onDeleteArtifact, onRenameArtifact, isAuthenticated, artifactVisibility, artifactOpacity, onToggleArtifactVisibility, onSetArtifactOpacity, isArtifactVisible, getArtifactOpacity, onShowInfo, onFlyToArtifact, onEditArtifact, onDuplicateArtifact, onMoveArtifact, onReorderArtifacts, onReorderFolders, onMoveFolder, isAncestorHidden, isUserProjectWithDemoStatus, onShowExportUnlockPrompt, currentUserId, isClientViewer, }) => {
    var _a, _b;
    // Which rows this user may change. The rule itself lives in the capability
    // map so the folder tree and the Get Info dialogs read it identically.
    const canModifyArtifact = useCallback((artifact) => canModifyArtifactFor(artifact, currentUserId, isClientViewer), [isClientViewer, currentUserId]);
    // Sensors for drag-and-drop. PointerSensor with a small activation distance
    // prevents accidental drags from clicks; KeyboardSensor enables space-then-
    // arrows reordering for accessibility.
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    // Tracks the currently-dragged item (either an artifact or a folder) so we
    // can:
    //   - resolve the source folder (for cross-folder artifact drags)
    //   - skip the cross-folder highlight when the drag is just a folder
    //     reorder (folders dragged over folders shouldn't tint them as drop
    //     targets — the SortableContext's shift animation is the cue).
    const [activeDrag, setActiveDrag] = useState(null);
    // Latest dnd-kit `over.id`, kept in state so each rendered folder can
    // compute its own "I am the resolved drop target" cue. closestCenter
    // usually picks an inner sortable rather than the wrapping DroppableFolder,
    // so we have to resolve over.id → owning folder once and broadcast that
    // result.
    const [currentDragOverId, setCurrentDragOverId] = useState(null);
    const activeArtifactId = (activeDrag === null || activeDrag === void 0 ? void 0 : activeDrag.kind) === 'artifact' ? activeDrag.id : null;
    const sourceFolderId = activeArtifactId
        ? (_b = (_a = folders.find(f => f.artifacts.some(a => a.id === activeArtifactId))) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null
        : null;
    // Tree assembly: group flat folders by parent, sort each sibling set by
    // sortOrder, walk roots top-down assigning depth (1..MAX_FOLDER_DEPTH).
    const folderTree = useMemo(() => {
        var _a, _b;
        const byParent = new Map();
        for (const f of folders) {
            const key = (_a = f.parentFolderId) !== null && _a !== void 0 ? _a : null;
            const bucket = (_b = byParent.get(key)) !== null && _b !== void 0 ? _b : [];
            bucket.push(f);
            byParent.set(key, bucket);
        }
        for (const bucket of byParent.values()) {
            bucket.sort((a, b) => a.sortOrder - b.sortOrder);
        }
        const build = (parentKey, depth) => {
            var _a;
            const direct = (_a = byParent.get(parentKey)) !== null && _a !== void 0 ? _a : [];
            return direct.map(f => ({
                ...f,
                depth,
                children: build(f.id, depth + 1),
            }));
        };
        return build(null, 1);
    }, [folders]);
    // Parent lookup + descendant-id sets for cycle / depth checks during drag.
    // Computed once per render — for the modest folder counts in a project
    // this is cheap, and constant-time lookups during dragOver matter more.
    const folderById = useMemo(() => new Map(folders.map(f => [f.id, f])), [folders]);
    const descendantsByFolder = useMemo(() => {
        var _a;
        const childrenByParent = new Map();
        for (const f of folders) {
            if (f.parentFolderId) {
                const bucket = (_a = childrenByParent.get(f.parentFolderId)) !== null && _a !== void 0 ? _a : [];
                bucket.push(f.id);
                childrenByParent.set(f.parentFolderId, bucket);
            }
        }
        const out = new Map();
        const walk = (id, acc) => {
            var _a;
            for (const child of (_a = childrenByParent.get(id)) !== null && _a !== void 0 ? _a : []) {
                if (acc.has(child))
                    continue;
                acc.add(child);
                walk(child, acc);
            }
        };
        for (const f of folders) {
            const set = new Set();
            walk(f.id, set);
            out.set(f.id, set);
        }
        return out;
    }, [folders]);
    // Depth(folderId): 1-indexed, matches MAX_FOLDER_DEPTH semantics.
    const depthOf = useCallback((id) => {
        var _a, _b;
        let depth = 1;
        let cursor = (_a = folderById.get(id)) === null || _a === void 0 ? void 0 : _a.parentFolderId;
        while (cursor) {
            depth++;
            cursor = (_b = folderById.get(cursor)) === null || _b === void 0 ? void 0 : _b.parentFolderId;
        }
        return depth;
    }, [folderById]);
    // SubtreeHeight: 0 for leaf, 1 if direct children but no grandchildren, etc.
    const subtreeHeight = useCallback((id) => {
        var _a;
        const childrenByParent = new Map();
        for (const f of folders) {
            if (f.parentFolderId) {
                const bucket = (_a = childrenByParent.get(f.parentFolderId)) !== null && _a !== void 0 ? _a : [];
                bucket.push(f.id);
                childrenByParent.set(f.parentFolderId, bucket);
            }
        }
        const walk = (n) => {
            var _a;
            const kids = (_a = childrenByParent.get(n)) !== null && _a !== void 0 ? _a : [];
            if (kids.length === 0)
                return 0;
            let max = 0;
            for (const k of kids) {
                const h = walk(k) + 1;
                if (h > max)
                    max = h;
            }
            return max;
        };
        return walk(id);
    }, [folders]);
    // Validity check for a folder-into-folder drop: same-folder rejected,
    // cycles rejected, depth-exceeding rejected. Used by DnD preview + drop.
    const isFolderMoveValid = useCallback((movingId, newParentId) => {
        var _a;
        if (newParentId === null)
            return true; // move to root is always valid
        if (movingId === newParentId)
            return false;
        if ((_a = descendantsByFolder.get(movingId)) === null || _a === void 0 ? void 0 : _a.has(newParentId))
            return false;
        const parentDepth = depthOf(newParentId);
        const height = subtreeHeight(movingId);
        return parentDepth + 1 + height <= MAX_FOLDER_DEPTH;
    }, [descendantsByFolder, depthOf, subtreeHeight]);
    // Resolve `over.id` to the destination folder. Three shapes to handle:
    //   1. `folder-<id>` from a DroppableFolder zone
    //   2. A plain folder ULID — closestCenter picks the sortable folder
    //      header (registered with the raw ULID in the per-parent
    //      SortableContext) rather than the DroppableFolder when an artifact
    //      is dragged over a folder row. Without this branch the drop is
    //      shadowed by the sortable and dies silently.
    //   3. An artifact ULID — find the owning folder.
    const resolveTargetFolderId = useCallback((overId) => {
        var _a;
        if (overId == null)
            return null;
        const id = String(overId);
        if (id.startsWith('folder-'))
            return id.slice('folder-'.length);
        if (folders.some(f => f.id === id))
            return id;
        const f = folders.find(folder => folder.artifacts.some(a => a.id === id));
        return (_a = f === null || f === void 0 ? void 0 : f.id) !== null && _a !== void 0 ? _a : null;
    }, [folders]);
    const handleDragStart = useCallback((event) => {
        const id = String(event.active.id);
        // Distinguish folder drag from artifact drag by ID lookup. Both use raw
        // ULIDs as dnd-kit IDs (no prefix) — artifact and folder ULIDs don't
        // collide since they come from the same generator and are globally
        // unique, but we still resolve via the in-memory folders list rather
        // than guessing.
        const isFolder = folders.some(f => f.id === id);
        setActiveDrag(isFolder ? { kind: 'folder', id } : { kind: 'artifact', id });
        setCurrentDragOverId(null);
    }, [folders]);
    const handleDragOver = useCallback((event) => {
        setCurrentDragOverId(event.over ? String(event.over.id) : null);
    }, []);
    const handleDragEnd = useCallback(async (event) => {
        var _a, _b, _c, _d;
        const { active, over } = event;
        setActiveDrag(null);
        setCurrentDragOverId(null);
        if (!over)
            return;
        const activeId = String(active.id);
        const overId = String(over.id);
        // Folder drag — three target shapes, each with explicit intent.
        const activeIsFolder = folderById.has(activeId);
        if (activeIsFolder) {
            const activeParent = (_b = (_a = folderById.get(activeId)) === null || _a === void 0 ? void 0 : _a.parentFolderId) !== null && _b !== void 0 ? _b : null;
            // 1. Drop on a DroppableFolder zone (`folder-<id>`) → nest under it.
            if (overId.startsWith('folder-')) {
                const newParentId = overId.slice('folder-'.length);
                if (newParentId === activeId)
                    return;
                if (newParentId === activeParent)
                    return; // already a child of this folder
                if (!onMoveFolder || !isFolderMoveValid(activeId, newParentId))
                    return;
                try {
                    await onMoveFolder(activeId, newParentId);
                }
                catch (err) {
                    console.error('Failed to move folder', err);
                }
                return;
            }
            // 2. Drop on another folder's sortable header (plain folder ULID).
            if (folderById.has(overId)) {
                if (activeId === overId)
                    return;
                const overParent = (_d = (_c = folderById.get(overId)) === null || _c === void 0 ? void 0 : _c.parentFolderId) !== null && _d !== void 0 ? _d : null;
                if (activeParent === overParent) {
                    // Same sibling set → reorder.
                    if (!onReorderFolders)
                        return;
                    const siblingIds = folders
                        .filter(f => { var _a; return ((_a = f.parentFolderId) !== null && _a !== void 0 ? _a : null) === activeParent; })
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map(f => f.id);
                    const oldIndex = siblingIds.indexOf(activeId);
                    const newIndex = siblingIds.indexOf(overId);
                    if (oldIndex < 0 || newIndex < 0)
                        return;
                    const reordered = arrayMove(siblingIds, oldIndex, newIndex);
                    try {
                        await onReorderFolders(reordered, activeParent);
                    }
                    catch (err) {
                        console.error('Failed to reorder folders', err);
                    }
                    return;
                }
                // Different parent → nest under the over folder, not "adopt its parent".
                if (!onMoveFolder || !isFolderMoveValid(activeId, overId))
                    return;
                try {
                    await onMoveFolder(activeId, overId);
                }
                catch (err) {
                    console.error('Failed to move folder', err);
                }
                return;
            }
            // 3. Drop on an artifact row → resolve to the artifact's owning folder
            //    and nest under it. This was the regression: previously the code
            //    treated artifact IDs as folders, got `undefined → null` for the
            //    parent, and moved the folder to root.
            const containingFolder = folders.find(f => f.artifacts.some(a => a.id === overId));
            if (containingFolder) {
                if (containingFolder.id === activeId)
                    return; // can't nest folder inside itself
                if (containingFolder.id === activeParent)
                    return; // already a child of this folder
                if (!onMoveFolder || !isFolderMoveValid(activeId, containingFolder.id))
                    return;
                try {
                    await onMoveFolder(activeId, containingFolder.id);
                }
                catch (err) {
                    console.error('Failed to move folder', err);
                }
                return;
            }
            // 4. Unknown target — bail.
            return;
        }
        // Otherwise it's an artifact drag.
        const targetFolderId = resolveTargetFolderId(over.id);
        const sourceFolder = folders.find(f => f.artifacts.some(a => a.id === activeId));
        if (!sourceFolder || !targetFolderId)
            return;
        if (sourceFolder.id === targetFolderId) {
            // Same-folder reorder. Skip if dropping onto self.
            if (active.id === over.id || !onReorderArtifacts)
                return;
            const currentIds = sourceFolder.artifacts.map(a => a.id);
            const oldIndex = currentIds.indexOf(activeId);
            const newIndex = currentIds.indexOf(String(over.id));
            if (oldIndex < 0 || newIndex < 0)
                return;
            const reordered = arrayMove(currentIds, oldIndex, newIndex);
            try {
                await onReorderArtifacts(sourceFolder.id, reordered);
            }
            catch (err) {
                console.error('Failed to reorder artifacts', err);
            }
            return;
        }
        // Cross-folder: move via the existing service. Backend places the
        // artifact at the bottom of the destination folder's sort order.
        if (onMoveArtifact) {
            try {
                await onMoveArtifact(activeId, targetFolderId);
            }
            catch (err) {
                console.error('Failed to move artifact', err);
            }
        }
    }, [folders, folderById, isFolderMoveValid, onMoveFolder, onReorderArtifacts, onReorderFolders, onMoveArtifact, resolveTargetFolderId]);
    const handleDragCancel = useCallback(() => {
        setActiveDrag(null);
        setCurrentDragOverId(null);
    }, []);
    // Display data for the cursor-following <DragOverlay> chip. Looks up the
    // dragged item's name + colour regardless of whether it's an artifact or
    // a folder; returns null when no drag is active.
    const dragChipInfo = (() => {
        if (!activeDrag)
            return null;
        if (activeDrag.kind === 'folder') {
            const f = folders.find(folder => folder.id === activeDrag.id);
            return f ? { name: f.name, color: f.color } : null;
        }
        for (const folder of folders) {
            const a = folder.artifacts.find(art => art.id === activeDrag.id);
            if (a)
                return { name: a.name, color: a.color };
        }
        return null;
    })();
    // Global grabbing cursor for the duration of a drag — without this the
    // cursor reverts to default when leaving the source row, which feels off
    // since the cursor-following chip is still visible.
    useEffect(() => {
        if (activeDrag) {
            const prev = document.body.style.cursor;
            document.body.style.cursor = 'grabbing';
            return () => { document.body.style.cursor = prev; };
        }
    }, [activeDrag]);
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    // Subfolder creation: tracks which parent folder is currently expanded
    // into the inline name input, plus the in-progress name. Single-tenant —
    // only one subfolder creation can be open at a time, matching how root
    // folder creation works.
    const [subfolderCreatingUnder, setSubfolderCreatingUnder] = useState(null);
    const [subfolderNewName, setSubfolderNewName] = useState('');
    const [pendingDeleteFolder, setPendingDeleteFolder] = useState(null);
    // Which artifact's opacity slider is extended below its row (one at a time;
    // mirrors FolderTree's SliderUnderBar row-extension pattern).
    const [openOpacityFor, setOpenOpacityFor] = useState(null);
    const [pendingDeleteArtifact, setPendingDeleteArtifact] = useState(null);
    const [renamingFolder, setRenamingFolder] = useState(null);
    const [renameFolderValue, setRenameFolderValue] = useState('');
    const [colorPickerFolder, setColorPickerFolder] = useState(null);
    const renameInputRef = useRef(null);
    // Inline artifact rename (right-click → Rename).
    const [renamingArtifact, setRenamingArtifact] = useState(null);
    const [renameArtifactValue, setRenameArtifactValue] = useState('');
    const commitArtifactRename = useCallback(async (artifactId) => {
        const name = renameArtifactValue.trim();
        setRenamingArtifact(null);
        if (name && onRenameArtifact) {
            try {
                await onRenameArtifact(artifactId, name);
            }
            catch (err) {
                console.error('Failed to rename artifact:', err);
            }
        }
    }, [renameArtifactValue, onRenameArtifact]);
    const contextMenu = useContextMenu();
    // Export gates for demo projects. On a user-owned demo, drawing exports
    // short-circuit to the unlock conversion modal instead of producing a file
    // — matching the LandScope-layer and main-KMZ download gates.
    const tryExportArtifact = useCallback((artifact, format) => {
        contextMenu.hideMenu();
        if (isUserProjectWithDemoStatus && onShowExportUnlockPrompt) {
            onShowExportUnlockPrompt();
            return;
        }
        exportUserArtifact(artifact, format);
    }, [contextMenu, isUserProjectWithDemoStatus, onShowExportUnlockPrompt]);
    const tryExportFolder = useCallback((folderId, exportNode, format) => {
        contextMenu.hideMenu();
        if (isUserProjectWithDemoStatus && onShowExportUnlockPrompt) {
            onShowExportUnlockPrompt();
            return;
        }
        exportFolder(folderId, exportNode, format);
    }, [contextMenu, isUserProjectWithDemoStatus, onShowExportUnlockPrompt]);
    // Right-click menu for a saved artifact. Get Info opens the canonical
    // options dialog (rename, colour, pop-out profile, etc); the rest are
    // direct shortcuts to common actions. Items only appear if their handler
    // is wired upstream — keeps this component agnostic to which features the
    // current surface supports.
    const handleArtifactRightClick = useCallback((e, artifact) => {
        e.preventDefault();
        e.stopPropagation();
        // Anchor the menu to the row's right edge with a small gap, top-aligned
        // with the row. Predictable placement is preferable to cursor-relative
        // here since the panel is narrow and cursor positions cluster near the
        // left edge.
        const rowRect = e.currentTarget.getBoundingClientRect();
        const x = rowRect.right + 4;
        const y = rowRect.top;
        const dialogX = e.clientX;
        const dialogY = e.clientY;
        const visible = isArtifactVisible(artifact.id);
        // Actions that change the row are dropped entirely for a client viewer
        // looking at someone else's drawing; what's left is view-only.
        const mutable = canModifyArtifact(artifact);
        const items = [];
        // Sketch has no meaningful info panel — skip Get Info for it.
        if (onShowInfo && artifact.artifactType !== 'sketch') {
            items.push({
                id: `info-${artifact.id}`, label: 'Get Info', icon: Info,
                action: () => { onShowInfo(artifact, { x: dialogX, y: dialogY }); contextMenu.hideMenu(); },
            });
        }
        if (onFlyToArtifact) {
            items.push({
                id: `flyto-${artifact.id}`, label: 'Fly to', icon: Crosshair,
                action: () => { onFlyToArtifact(artifact.id); contextMenu.hideMenu(); },
            });
        }
        if (onEditArtifact && mutable) {
            items.push({
                id: `edit-${artifact.id}`, label: 'Edit', icon: Edit3,
                action: () => { onEditArtifact(artifact); contextMenu.hideMenu(); },
            });
        }
        if (onRenameArtifact && mutable) {
            items.push({
                id: `rename-${artifact.id}`, label: 'Rename', icon: Pencil,
                action: () => {
                    setRenameArtifactValue(artifact.name);
                    setRenamingArtifact(artifact.id);
                    contextMenu.hideMenu();
                },
            });
        }
        items.push({
            id: `toggle-${artifact.id}`, label: visible ? 'Hide' : 'Show', icon: visible ? EyeOff : Eye,
            action: () => { onToggleArtifactVisibility(artifact.id); contextMenu.hideMenu(); },
        });
        if (onDuplicateArtifact && mutable) {
            items.push({
                id: `dup-${artifact.id}`, label: 'Duplicate', icon: Copy,
                action: () => { onDuplicateArtifact(artifact); contextMenu.hideMenu(); },
            });
        }
        if (onMoveArtifact && mutable && folders.length > 1) {
            // Submenu lists every folder except the artifact's current one — moving
            // an item into the folder it already lives in is a no-op.
            const targets = folders.filter(f => f.id !== artifact.folderId);
            if (targets.length > 0) {
                items.push({
                    id: `move-${artifact.id}`,
                    label: 'Move to folder',
                    icon: FolderInput,
                    action: () => { },
                    submenu: targets.map(f => ({
                        id: `move-${artifact.id}-${f.id}`,
                        label: f.name,
                        icon: Folder,
                        action: () => { onMoveArtifact(artifact.id, f.id); contextMenu.hideMenu(); },
                    })),
                });
            }
        }
        // Export As submenu. Client-side formats (KML / GeoJSON / GPX) emit
        // directly from the artifact's inline geometryJson. Phase 3 server-
        // side formats (Shapefile / SVG / DXF) route through the
        // ExportUserArtifact RPC, which projects (Mercator for SVG, UTM for
        // DXF) and uses ogr2ogr for Shapefile.
        // GPX has no polygon primitive, so it's hidden for polygon artifacts.
        const exportSubmenu = [
            {
                id: `export-kml-${artifact.id}`, label: 'KML', icon: Download,
                action: () => tryExportArtifact(artifact, 'kml'),
            },
            {
                id: `export-geojson-${artifact.id}`, label: 'GeoJSON', icon: Download,
                action: () => tryExportArtifact(artifact, 'geojson'),
            },
        ];
        if (artifact.artifactType !== 'polygon') {
            exportSubmenu.push({
                id: `export-gpx-${artifact.id}`, label: 'GPX', icon: Download,
                action: () => tryExportArtifact(artifact, 'gpx'),
            });
        }
        exportSubmenu.push({
            id: `export-shapefile-${artifact.id}`, label: 'Shapefile (.shp)', icon: Download,
            action: () => tryExportArtifact(artifact, 'shapefile'),
        }, {
            id: `export-svg-${artifact.id}`, label: 'SVG', icon: Download,
            action: () => tryExportArtifact(artifact, 'svg'),
        }, {
            id: `export-dxf-${artifact.id}`, label: 'DXF', icon: Download,
            action: () => tryExportArtifact(artifact, 'dxf'),
        });
        // Export isn't wired for sketches (their multi-stroke geometry has no
        // single-primitive export yet), so don't offer a broken action.
        if (artifact.artifactType !== 'sketch') {
            items.push({
                id: `export-${artifact.id}`,
                label: 'Export As',
                icon: Download,
                action: () => { },
                submenu: exportSubmenu,
            });
        }
        if (mutable) {
            items.push({
                id: `delete-${artifact.id}`, label: 'Delete', icon: Trash2,
                action: () => { setPendingDeleteArtifact(artifact.id); contextMenu.hideMenu(); },
            });
        }
        contextMenu.showMenu({ x, y }, items);
    }, [contextMenu, onShowInfo, onFlyToArtifact, onEditArtifact, onRenameArtifact, onDuplicateArtifact, onMoveArtifact, folders, isArtifactVisible, onToggleArtifactVisibility, tryExportArtifact, canModifyArtifact]);
    // Builds the nested export tree for a folder and all of its descendant
    // subfolders, sorted by sortOrder at each level. KML reproduces this as
    // nested <Folder> elements; GeoJSON / GPX flatten it. Mirrors the backend
    // subtree walk so client- and server-side folder exports cover the same
    // set of features.
    const buildFolderExportNode = useCallback((rootId) => {
        var _a;
        const childrenByParent = new Map();
        for (const f of folders) {
            if (f.parentFolderId) {
                const bucket = (_a = childrenByParent.get(f.parentFolderId)) !== null && _a !== void 0 ? _a : [];
                bucket.push(f);
                childrenByParent.set(f.parentFolderId, bucket);
            }
        }
        const build = (id) => {
            var _a, _b, _c;
            const folder = folders.find(f => f.id === id);
            const children = ((_a = childrenByParent.get(id)) !== null && _a !== void 0 ? _a : [])
                .slice()
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(child => build(child.id));
            return {
                name: (_b = folder === null || folder === void 0 ? void 0 : folder.name) !== null && _b !== void 0 ? _b : 'Folder',
                artifacts: (_c = folder === null || folder === void 0 ? void 0 : folder.artifacts) !== null && _c !== void 0 ? _c : [],
                children,
            };
        };
        return build(rootId);
    }, [folders]);
    // Right-click on a folder header. Mirrors the Google Earth Pro pattern:
    // every folder action lives in one menu so the row itself can stay clean.
    // depth is a parameter (not closed-over) so this can stay defined at the
    // component top level — depth is only known inside renderFolder.
    const handleFolderRightClick = useCallback((e, folder, depth) => {
        e.preventDefault();
        e.stopPropagation();
        const rowRect = e.currentTarget.getBoundingClientRect();
        const x = rowRect.right + 4;
        const y = rowRect.top;
        const items = [];
        if (depth < MAX_FOLDER_DEPTH) {
            items.push({
                id: `add-sub-${folder.id}`,
                label: 'Add subfolder',
                icon: FolderPlus,
                action: () => {
                    setSubfolderCreatingUnder(folder.id);
                    setSubfolderNewName('');
                    contextMenu.hideMenu();
                },
            });
        }
        items.push({
            id: `rename-${folder.id}`,
            label: 'Rename',
            icon: Pencil,
            action: () => {
                setRenameFolderValue(folder.name);
                setRenamingFolder(folder.id);
                contextMenu.hideMenu();
            },
        });
        items.push({
            id: `color-${folder.id}`,
            label: 'Change color',
            icon: Palette,
            action: () => {
                setColorPickerFolder(folder.id);
                contextMenu.hideMenu();
            },
        });
        // Export As — exports every feature in this folder and its subfolders.
        // KML / GeoJSON / GPX assemble client-side into one file; Shapefile / SVG
        // / DXF round-trip through the ExportUserFolder RPC (server zips / draws).
        // Only shown when the subtree actually has features; GPX is omitted unless
        // there's a non-polygon feature (polygons have no GPX representation),
        // mirroring the per-artifact menu.
        const exportNode = buildFolderExportNode(folder.id);
        const collectArtifacts = (n) => [...n.artifacts, ...n.children.flatMap(collectArtifacts)];
        const subtreeArtifacts = collectArtifacts(exportNode);
        if (subtreeArtifacts.length > 0) {
            const hasNonPolygon = subtreeArtifacts.some(a => a.artifactType !== 'polygon' && a.artifactType !== 'measurement_circle');
            const exportSubmenu = [
                {
                    id: `folder-export-kml-${folder.id}`, label: 'KML', icon: Download,
                    action: () => tryExportFolder(folder.id, exportNode, 'kml'),
                },
                {
                    id: `folder-export-geojson-${folder.id}`, label: 'GeoJSON', icon: Download,
                    action: () => tryExportFolder(folder.id, exportNode, 'geojson'),
                },
            ];
            if (hasNonPolygon) {
                exportSubmenu.push({
                    id: `folder-export-gpx-${folder.id}`, label: 'GPX', icon: Download,
                    action: () => tryExportFolder(folder.id, exportNode, 'gpx'),
                });
            }
            exportSubmenu.push({
                id: `folder-export-shapefile-${folder.id}`, label: 'Shapefile (.shp)', icon: Download,
                action: () => tryExportFolder(folder.id, exportNode, 'shapefile'),
            }, {
                id: `folder-export-svg-${folder.id}`, label: 'SVG', icon: Download,
                action: () => tryExportFolder(folder.id, exportNode, 'svg'),
            }, {
                id: `folder-export-dxf-${folder.id}`, label: 'DXF', icon: Download,
                action: () => tryExportFolder(folder.id, exportNode, 'dxf'),
            });
            items.push({
                id: `folder-export-${folder.id}`,
                label: 'Export As',
                icon: Download,
                action: () => { },
                submenu: exportSubmenu,
            });
        }
        items.push({
            id: `delete-${folder.id}`,
            label: 'Delete',
            icon: Trash2,
            action: () => {
                setPendingDeleteFolder(folder.id);
                contextMenu.hideMenu();
            },
        });
        contextMenu.showMenu({ x, y }, items);
    }, [contextMenu, buildFolderExportNode, tryExportFolder]);
    // Folder colour palette — same nine slots as TOOL_COLORS so the shared
    // ColorPicker's `grid-cols-9` lays out identically to the path edit modal.
    // Colours mirror the path/measurement palette for cross-tool consistency.
    const FOLDER_COLOR_OPTIONS = [
        { color: '#DC143C', name: 'Red' },
        { color: '#FF6B35', name: 'Orange' },
        { color: '#FFD700', name: 'Yellow' },
        { color: '#32CD32', name: 'Green' },
        { color: '#00CED1', name: 'Cyan' },
        { color: '#1E90FF', name: 'Blue' },
        { color: '#9370DB', name: 'Purple' },
        { color: '#FF69B4', name: 'Pink' },
        { color: '#FFFFFF', name: 'White' },
    ];
    const handleRenameFolder = useCallback(async (folderId) => {
        if (!renameFolderValue.trim()) {
            setRenamingFolder(null);
            return;
        }
        try {
            await onUpdateFolder(folderId, { name: renameFolderValue.trim() });
        }
        catch (err) {
            console.error('Failed to rename folder:', err);
        }
        setRenamingFolder(null);
    }, [renameFolderValue, onUpdateFolder]);
    const handleColorChange = useCallback(async (folderId, color) => {
        try {
            await onUpdateFolder(folderId, { color });
        }
        catch (err) {
            console.error('Failed to update folder color:', err);
        }
        setColorPickerFolder(null);
    }, [onUpdateFolder]);
    const handleCreateFolder = useCallback(async () => {
        if (!newFolderName.trim())
            return;
        try {
            await onCreateFolder(newFolderName.trim());
            setNewFolderName('');
            setIsCreatingFolder(false);
        }
        catch (err) {
            console.error('Failed to create folder:', err);
        }
    }, [newFolderName, onCreateFolder]);
    const handleCreateSubfolder = useCallback(async (parentFolderId, parentColor) => {
        if (!subfolderNewName.trim())
            return;
        try {
            await onCreateFolder(subfolderNewName.trim(), parentColor, parentFolderId);
            setSubfolderNewName('');
            setSubfolderCreatingUnder(null);
        }
        catch (err) {
            console.error('Failed to create subfolder:', err);
        }
    }, [subfolderNewName, onCreateFolder]);
    const handleSubfolderKeyDown = useCallback((e, parentFolderId, parentColor) => {
        if (e.key === 'Enter') {
            handleCreateSubfolder(parentFolderId, parentColor);
        }
        else if (e.key === 'Escape') {
            setSubfolderCreatingUnder(null);
            setSubfolderNewName('');
        }
    }, [handleCreateSubfolder]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            handleCreateFolder();
        }
        else if (e.key === 'Escape') {
            setIsCreatingFolder(false);
            setNewFolderName('');
        }
    }, [handleCreateFolder]);
    const isFolderVisible = (folderId) => folderVisibility[folderId] !== false;
    const isFolderCollapsed = (folderId) => folderCollapsed[folderId] === true;
    const getVisibleArtifactCount = (folder) => {
        return folder.artifacts.filter(a => isArtifactVisible(a.id)).length;
    };
    const handleToggleFolderArtifacts = (folder) => {
        const visibleCount = getVisibleArtifactCount(folder);
        const hasVisibleArtifacts = visibleCount > 0;
        folder.artifacts.forEach(artifact => {
            if (hasVisibleArtifacts) {
                if (isArtifactVisible(artifact.id)) {
                    onToggleArtifactVisibility(artifact.id);
                }
            }
            else {
                if (!isArtifactVisible(artifact.id)) {
                    onToggleArtifactVisibility(artifact.id);
                }
            }
        });
    };
    return (_jsxs("div", { children: [isAuthenticated && !isCreatingFolder && (_jsxs("button", { onClick: () => setIsCreatingFolder(true), className: "flex items-center gap-1.5 mt-2 mb-1 px-2 py-1 rounded text-xs transition-colors", style: { color: 'var(--dew)', backgroundColor: 'transparent' }, onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = 'var(--pewter)'; }, onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; }, children: [_jsx(FolderPlus, { size: 14 }), _jsx("span", { children: "New Folder" })] })), isCreatingFolder && (_jsxs("div", { className: "mb-2 flex gap-2", children: [_jsx("input", { type: "text", value: newFolderName, onChange: (e) => setNewFolderName(e.target.value), onKeyDown: handleKeyDown, placeholder: "Folder name...", autoFocus: true, className: "flex-1 px-2 py-1 text-xs rounded border focus:outline-none focus:ring-1", style: {
                            backgroundColor: 'var(--black)',
                            borderColor: 'var(--pewter)',
                            color: 'var(--sand)'
                        } }), _jsx("button", { onClick: handleCreateFolder, disabled: !newFolderName.trim(), className: "px-2 py-1 text-xs rounded transition-colors disabled:opacity-50", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: "Create" })] })), isLoading && folders.length === 0 && (_jsx("div", { className: "p-3 rounded text-xs text-center", style: { backgroundColor: 'var(--charcoal)', color: 'var(--pewter)' }, children: "Loading folders..." })), error && (_jsx("div", { className: "p-3 rounded text-xs text-center", style: { backgroundColor: 'var(--charcoal)', color: '#ef4444' }, children: error })), !isLoading && !error && folders.length === 0 && (_jsx("div", { className: "p-3 rounded text-xs text-center", style: { backgroundColor: 'var(--charcoal)', color: 'var(--pewter)' }, children: isAuthenticated
                    ? 'No folders yet. Use the tools above to create annotations and save them here.'
                    : 'Sign in to save your annotations to folders.' })), folderTree.length > 0 && (() => {
                // Render a single folder node (header + body + recursive children).
                // Sibling-scoped reorder works because each folder's children are wrapped
                // in their own SortableContext over the child IDs (see body below).
                const renderFolder = (node) => {
                    const folder = node;
                    const depth = node.depth;
                    const isExpanded = !isFolderCollapsed(folder.id);
                    const hasArtifacts = folder.artifacts.length > 0;
                    const hasChildFolders = node.children.length > 0;
                    const visibleCount = getVisibleArtifactCount(folder);
                    const hasVisibleArtifacts = visibleCount > 0;
                    // Highlight + auto-expand only fire on cross-folder drags. An
                    // active folder drag of THIS folder, or an artifact from this same
                    // folder, shouldn't tint it as a drop target.
                    const isCrossFolderArtifactDrag = !!sourceFolderId && sourceFolderId !== folder.id;
                    const isFolderDragActive = (activeDrag === null || activeDrag === void 0 ? void 0 : activeDrag.kind) === 'folder';
                    const isCrossFolderFolderDrag = isFolderDragActive
                        && activeDrag.id !== folder.id
                        && isFolderMoveValid(activeDrag.id, folder.id);
                    const isCrossFolderDrag = isCrossFolderArtifactDrag || isCrossFolderFolderDrag;
                    // Folder drag handle enabled when re-parent or sibling reorder is
                    // possible. With nesting, even a single root folder can be dragged
                    // into another folder (re-parent), so we no longer gate on count > 1.
                    const folderDragEnabled = !!onReorderFolders || !!onMoveFolder;
                    const ancestorHidden = isAncestorHidden ? isAncestorHidden(folder.id) : false;
                    // Resolve the current cursor target through the same helper
                    // handleDragEnd uses, so the highlight follows the cursor across
                    // folder header / droppable zone / contained artifact rows. This is
                    // the signal DroppableFolder.forceHighlight reads.
                    const resolvedTargetFolderId = currentDragOverId
                        ? resolveTargetFolderId(currentDragOverId)
                        : null;
                    const isResolvedDropTarget = resolvedTargetFolderId === folder.id;
                    return (
                    // Depth-based indent on the outer wrapper so the entire row —
                    // charcoal background, drop highlight outline, drag handle — all
                    // shift right. Previously the indent was only on the header's
                    // inner content (paddingLeft), which left the row's background
                    // hugging the parent's left edge and made subfolders look
                    // un-nested.
                    _jsx("div", { style: depth > 1 ? { marginLeft: 16 } : undefined, children: _jsx(DroppableFolder, { folderId: folder.id, isCrossFolderDrag: isCrossFolderDrag, forceHighlight: isCrossFolderDrag && isResolvedDropTarget, isCollapsed: !isExpanded, onAutoExpand: () => onToggleFolderCollapsed(folder.id), 
                            // Disable this folder as a drop target if a folder drag is
                            // active AND landing here would be invalid (cycle / depth).
                            // Artifact drags fall back to the original disable rule.
                            disabled: isFolderDragActive && (activeDrag.id === folder.id
                                || !isFolderMoveValid(activeDrag.id, folder.id)), children: _jsx(SortableFolderHeader, { id: folder.id, disabled: !folderDragEnabled, children: (folderDragHandle) => (_jsxs("div", { className: "group", style: { opacity: ancestorHidden ? 0.55 : 1 }, children: [_jsxs("div", { className: "flex items-center gap-2 px-2 py-1 text-xs rounded cursor-pointer hover:bg-opacity-80 transition-colors", style: {
                                                backgroundColor: 'var(--charcoal)',
                                                color: 'var(--dew)'
                                            }, onClick: () => onToggleFolderCollapsed(folder.id), onContextMenu: (e) => handleFolderRightClick(e, folder, depth), title: ancestorHidden ? 'Hidden because parent folder is hidden' : undefined, children: [folderDragHandle && (_jsx("div", { onClick: (e) => e.stopPropagation(), children: folderDragHandle })), (hasArtifacts || hasChildFolders) ? (isExpanded ? _jsx(ChevronDown, { size: 12 }) : _jsx(ChevronRight, { size: 12 })) : (_jsx("div", { className: "w-3" })), isExpanded ? _jsx(FolderOpen, { size: 12, style: { color: folder.color } }) : _jsx(Folder, { size: 12, style: { color: folder.color } }), renamingFolder === folder.id ? (_jsx("input", { ref: renameInputRef, type: "text", value: renameFolderValue, onChange: (e) => setRenameFolderValue(e.target.value), onKeyDown: (e) => {
                                                        if (e.key === 'Enter')
                                                            handleRenameFolder(folder.id);
                                                        if (e.key === 'Escape')
                                                            setRenamingFolder(null);
                                                        e.stopPropagation();
                                                    }, onBlur: () => handleRenameFolder(folder.id), onClick: (e) => e.stopPropagation(), autoFocus: true, className: "flex-1 min-w-0 px-1 py-0 text-xs rounded border focus:outline-none", style: {
                                                        backgroundColor: 'var(--black)',
                                                        borderColor: 'var(--pewter)',
                                                        color: 'var(--sand)'
                                                    } })) : (_jsxs(_Fragment, { children: [folder.owner && folder.owner.userId !== currentUserId && (_jsx(AuthorChip, { author: folder.owner, title: `Folder by ${getDisplayName(folder.owner)}` })), _jsx("span", { className: "flex-1 truncate", children: folder.name })] })), hasArtifacts && (_jsxs("button", { onClick: (e) => {
                                                        e.stopPropagation();
                                                        handleToggleFolderArtifacts(folder);
                                                    }, className: `px-1 py-0.5 text-xs rounded flex items-center justify-center gap-1 whitespace-nowrap ${hasVisibleArtifacts
                                                        ? 'bg-green-600 hover:bg-green-700'
                                                        : 'bg-gray-600 hover:bg-gray-700'}`, style: { minWidth: '4rem' }, title: hasVisibleArtifacts
                                                        ? `Hide ${visibleCount} visible item${visibleCount !== 1 ? 's' : ''}`
                                                        : `Show ${folder.artifacts.length} item${folder.artifacts.length !== 1 ? 's' : ''}`, children: [hasVisibleArtifacts ? _jsx(ScanLine, { size: 12 }) : _jsx(ScanEye, { size: 12 }), hasVisibleArtifacts ? `Hide ${visibleCount}` : `Show ${folder.artifacts.length}`] })), _jsxs("div", { className: `flex items-center gap-1 transition-opacity ${renamingFolder === folder.id
                                                        || colorPickerFolder === folder.id
                                                        || pendingDeleteFolder === folder.id
                                                        ? 'opacity-100'
                                                        : 'opacity-0 group-hover:opacity-100 focus-within:opacity-100'}`, onClick: (e) => e.stopPropagation(), children: [depth < MAX_FOLDER_DEPTH && (_jsx("button", { onClick: (e) => {
                                                                e.stopPropagation();
                                                                setSubfolderCreatingUnder(folder.id);
                                                                setSubfolderNewName('');
                                                            }, className: "p-0.5 rounded transition-colors hover:bg-emerald-500 hover:bg-opacity-20", style: { color: 'var(--pewter)' }, title: "Add subfolder", children: _jsx(FolderPlus, { size: 12 }) })), renamingFolder !== folder.id && (_jsx("button", { onClick: (e) => {
                                                                e.stopPropagation();
                                                                setRenameFolderValue(folder.name);
                                                                setRenamingFolder(folder.id);
                                                            }, className: "p-0.5 rounded transition-colors hover:bg-blue-500 hover:bg-opacity-20", style: { color: 'var(--pewter)' }, title: "Rename folder", children: _jsx(Pencil, { size: 12 }) })), _jsxs("div", { className: "relative", children: [_jsx("button", { onClick: (e) => {
                                                                        e.stopPropagation();
                                                                        setColorPickerFolder(colorPickerFolder === folder.id ? null : folder.id);
                                                                    }, className: "p-0.5 rounded transition-colors hover:bg-purple-500 hover:bg-opacity-20", style: { color: folder.color }, title: "Change folder color", children: _jsx(Palette, { size: 12 }) }), colorPickerFolder === folder.id && (_jsx("div", { className: "absolute right-0 top-full mt-1 p-2 rounded shadow-lg border z-[70] w-[260px]", style: { backgroundColor: 'var(--charcoal)', borderColor: 'var(--pewter)' }, onClick: (e) => e.stopPropagation(), children: _jsx(ColorPicker, { colors: FOLDER_COLOR_OPTIONS, selectedColor: folder.color, onColorChange: (c) => handleColorChange(folder.id, c) }) }))] }), pendingDeleteFolder === folder.id ? (_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("button", { onClick: () => {
                                                                        onDeleteFolder(folder.id);
                                                                        setPendingDeleteFolder(null);
                                                                    }, className: "px-1.5 py-0.5 text-[10px] rounded transition-colors", style: { backgroundColor: '#DC143C', color: 'white' }, children: "Delete" }), _jsx("button", { onClick: () => setPendingDeleteFolder(null), className: "px-1.5 py-0.5 text-[10px] rounded transition-colors", style: { backgroundColor: 'var(--pewter)', color: 'var(--sand)' }, children: "Cancel" })] })) : (_jsx("button", { onClick: (e) => {
                                                                e.stopPropagation();
                                                                setPendingDeleteFolder(folder.id);
                                                            }, className: "p-0.5 rounded transition-colors hover:bg-red-500 hover:bg-opacity-20", style: { color: 'var(--pewter)' }, title: "Delete folder", children: _jsx(Trash2, { size: 12 }) }))] })] }), isExpanded && (() => {
                                            const artifactIds = folder.artifacts.map(a => a.id);
                                            // Drag handle is enabled if the user could meaningfully drag
                                            // this row anywhere: either to reorder within the folder
                                            // (>1 artifact) or to move into another folder. The old
                                            // gate of just `>1 artifact` trapped lone artifacts inside
                                            // their folder once cross-folder drag was added.
                                            const reorderEnabled = (!!onReorderArtifacts && folder.artifacts.length > 1) ||
                                                (!!onMoveArtifact && folders.length > 1);
                                            const childIds = node.children.map(c => c.id);
                                            // Child indent inside this folder's body. Stays fixed at 16
                                            // because depth-based offsets now live on each folder's outer
                                            // wrapper (see the depth > 1 marginLeft on the renderFolder
                                            // return), so the body itself doesn't need to grow padding
                                            // per-level.
                                            const childrenPaddingLeft = 16;
                                            return (
                                            // `mt-1 space-y-1` gives consistent 4px spacing: a gap
                                            // between the folder header and its body, and equal gaps
                                            // between every body element (artifact rows, subfolder
                                            // creation affordance, subfolder list). Replaces the
                                            // per-row `mb-1` that left first-children flush with the
                                            // parent body top.
                                            _jsxs("div", { className: "mt-1 space-y-1", children: [_jsx(SortableContext, { items: artifactIds, strategy: verticalListSortingStrategy, children: _jsxs("div", { children: [folder.artifacts.map((artifact) => {
                                                                    const visible = isArtifactVisible(artifact.id);
                                                                    const opacity = getArtifactOpacity(artifact.id);
                                                                    // Same rule as the context menu: a client viewer
                                                                    // cannot move or delete a drawing someone else
                                                                    // made, so the drag handle and the row's Delete
                                                                    // button are not rendered for those rows.
                                                                    const mutable = canModifyArtifact(artifact);
                                                                    return (_jsx(SortableArtifactRow, { id: artifact.id, disabled: !reorderEnabled || !mutable, dropTargetDisabled: (activeDrag === null || activeDrag === void 0 ? void 0 : activeDrag.kind) === 'folder', children: (dragHandle) => (_jsxs("div", { className: "group p-2 ml-4 mb-1 bg-gray-800/50 rounded", style: { marginLeft: '16px' }, onContextMenu: (e) => handleArtifactRightClick(e, artifact), children: [_jsxs("div", { className: "flex items-start gap-2", children: [dragHandle && (_jsx("div", { className: "flex items-center pt-0.5 flex-shrink-0", children: dragHandle })), _jsx("div", { className: "flex-1 min-w-0", children: _jsxs("div", { className: "flex items-center gap-2", children: [(() => {
                                                                                                        const Icon = ARTIFACT_TYPE_ICONS[artifact.artifactType];
                                                                                                        return Icon ? (_jsx(Icon, { size: 14, style: { color: 'var(--dew)', flexShrink: 0 } })) : null;
                                                                                                    })(), artifact.author && artifact.author.userId !== currentUserId && (_jsx(AuthorChip, { author: artifact.author, title: `Drawn by ${getDisplayName(artifact.author)}` })), renamingArtifact === artifact.id ? (_jsx("input", { autoFocus: true, value: renameArtifactValue, onChange: (e) => setRenameArtifactValue(e.target.value), onClick: (e) => e.stopPropagation(), onKeyDown: (e) => {
                                                                                                            if (e.key === 'Enter')
                                                                                                                commitArtifactRename(artifact.id);
                                                                                                            else if (e.key === 'Escape')
                                                                                                                setRenamingArtifact(null);
                                                                                                        }, onBlur: () => commitArtifactRename(artifact.id), className: "text-xs font-medium flex-1 min-w-0 px-1 py-0.5 rounded outline-none", style: { backgroundColor: 'var(--pewter)', color: 'var(--sand)', border: '1px solid var(--charcoal)' } })) : (_jsx("button", { type: "button", onClick: (e) => {
                                                                                                            e.stopPropagation();
                                                                                                            if (onShowInfo && artifact.artifactType !== 'sketch')
                                                                                                                onShowInfo(artifact, { x: e.clientX, y: e.clientY });
                                                                                                        }, className: "text-xs font-medium truncate text-left flex-1 min-w-0", style: {
                                                                                                            color: 'var(--sand)',
                                                                                                            background: 'none',
                                                                                                            border: 'none',
                                                                                                            padding: 0,
                                                                                                            cursor: (onShowInfo && artifact.artifactType !== 'sketch') ? 'pointer' : 'default',
                                                                                                        }, title: (onShowInfo && artifact.artifactType !== 'sketch') ? 'Get info' : undefined, children: artifact.name }))] }) }), _jsxs("div", { className: "flex items-center gap-1", children: [visible && (_jsx(SliderChip, { label: "Opacity", value: opacity, defaultValue: 1, formatLabel: (v) => `${Math.round(v * 100)}%`, expanded: openOpacityFor === artifact.id, onToggle: () => setOpenOpacityFor(prev => (prev === artifact.id ? null : artifact.id)) })), !mutable ? null : pendingDeleteArtifact === artifact.id ? (_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("button", { onClick: () => {
                                                                                                                onDeleteArtifact(artifact.id);
                                                                                                                setPendingDeleteArtifact(null);
                                                                                                            }, className: "px-1.5 py-0.5 text-[10px] rounded transition-colors", style: { backgroundColor: '#DC143C', color: 'white' }, children: "Delete" }), _jsx("button", { onClick: () => setPendingDeleteArtifact(null), className: "px-1.5 py-0.5 text-[10px] rounded transition-colors", style: { backgroundColor: 'var(--pewter)', color: 'var(--sand)' }, children: "Cancel" })] })) : (_jsx("button", { onClick: () => setPendingDeleteArtifact(artifact.id), className: "w-6 h-6 rounded flex items-center justify-center transition-colors bg-gray-700 hover:bg-red-600", title: "Delete", children: _jsx(Trash2, { size: 12 }) })), _jsx("button", { onClick: () => onToggleArtifactVisibility(artifact.id), className: `w-6 h-6 rounded flex items-center justify-center ${visible ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'}`, title: visible ? 'Hide artifact' : 'Show artifact', "aria-label": visible ? 'Hide layer' : 'Show layer', children: visible ? _jsx(Eye, { size: 14 }) : _jsx(EyeOff, { size: 14 }) })] })] }), visible && openOpacityFor === artifact.id && (_jsx(SliderUnderBar, { label: "Opacity", value: opacity, defaultValue: 1, min: 0.1, max: 1, step: 0.1, formatLabel: (v) => `${Math.round(v * 100)}%`, onChange: (v) => onSetArtifactOpacity(artifact.id, v), onClose: () => setOpenOpacityFor(null), minIcon: _jsx(SunDim, { size: 10, className: "text-gray-400" }), maxIcon: _jsx(Sun, { size: 10, className: "text-gray-400" }) }))] })) }, artifact.id));
                                                                }), folder.artifacts.length === 0 && !hasChildFolders && (_jsx("div", { className: "ml-6 mr-2 mb-2 rounded text-[10px] flex items-center justify-center", style: {
                                                                        height: 44,
                                                                        border: '1px dashed var(--pewter)',
                                                                        color: 'var(--pewter)',
                                                                        opacity: 0.85,
                                                                    }, children: "Drop items here" }))] }) }), isAuthenticated
                                                        && depth < MAX_FOLDER_DEPTH
                                                        && subfolderCreatingUnder === folder.id && (_jsxs("div", { className: "flex gap-2", style: { marginLeft: `${childrenPaddingLeft}px`, marginRight: 8 }, children: [_jsx("input", { type: "text", value: subfolderNewName, onChange: (e) => setSubfolderNewName(e.target.value), onKeyDown: (e) => handleSubfolderKeyDown(e, folder.id, folder.color), onClick: (e) => e.stopPropagation(), placeholder: "Subfolder name...", autoFocus: true, className: "flex-1 min-w-0 px-2 py-1 text-xs rounded border focus:outline-none focus:ring-1", style: {
                                                                    backgroundColor: 'var(--black)',
                                                                    borderColor: 'var(--pewter)',
                                                                    color: 'var(--sand)',
                                                                } }), _jsx("button", { onClick: (e) => { e.stopPropagation(); handleCreateSubfolder(folder.id, folder.color); }, disabled: !subfolderNewName.trim(), className: "px-2 py-1 text-xs rounded transition-colors disabled:opacity-50", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: "Create" })] })), childIds.length > 0 && (_jsx(SortableContext, { items: childIds, strategy: verticalListSortingStrategy, children: _jsx("div", { className: "space-y-1", children: node.children.map(child => renderFolder(child)) }) }))] }));
                                        })()] })) }) }) }, folder.id));
                };
                // Root-level wrapper: outer SortableContext over root folder IDs so
                // dragging a root folder reorders among other root folders.
                const rootIds = folderTree.map(n => n.id);
                return (_jsxs(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: handleDragStart, onDragOver: handleDragOver, onDragEnd: handleDragEnd, onDragCancel: handleDragCancel, children: [_jsx(SortableContext, { items: rootIds, strategy: verticalListSortingStrategy, children: _jsx("div", { className: "space-y-1", children: folderTree.map(node => renderFolder(node)) }) }), _jsx(DragOverlay, { dropAnimation: null, children: dragChipInfo && (_jsxs("div", { style: {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '4px 10px',
                                    borderRadius: 6,
                                    backgroundColor: 'var(--charcoal)',
                                    border: `1px solid ${dragChipInfo.color}`,
                                    color: 'var(--sand)',
                                    fontSize: 12,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                                    pointerEvents: 'none',
                                    whiteSpace: 'nowrap',
                                    cursor: 'grabbing',
                                }, children: [_jsx("span", { style: {
                                            display: 'inline-block',
                                            width: 8,
                                            height: 8,
                                            borderRadius: 2,
                                            backgroundColor: dragChipInfo.color,
                                        } }), dragChipInfo.name] })) })] }));
            })(), _jsx(ContextMenu, { items: contextMenu.menuItems, position: contextMenu.position, onClose: contextMenu.hideMenu, isOpen: contextMenu.isOpen })] }));
};
